/* eslint-disable @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/no-unnecessary-condition, @typescript-eslint/prefer-optional-chain, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any */
import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Box, Chip, Loader, SidePanel } from '@saviynt/design-system';

import {
  SodFieldSet,
  SodTextFieldSet,
  SodUserChip,
} from '../../../../../../../shared/components';
import {
  ENTITLEMENT_DETAILS_TABS,
  testIds,
  violationsEntitlement,
} from '../../../../../../../shared/constants';
import {
  useGetQueryParams,
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../../../../../shared/hooks';
import { type EntitlementUsersSidePanelGroupRowContextState } from '../../../../../data-layer/detailsTypes';
import { detailsActions } from '../../../../../data-store/detailsSlice';
import { ViolationDonutChart } from '../../../../ViolationDonutChart';
import { useGetUsersMutation, type UserItem } from '../../data-layer';

import './UsersSidePanel.css';

interface UsersSidePanelProps {
  entKeys: number[];
  riskKeys: number[];
}

/**
 * UsersSidePanel Component
 *
 * Detailed sidepanel view for individual user records with comprehensive information
 * display, navigation capabilities, and quick access to related data and actions for
 * user management and analysis.
 *
 * @component
 * @returns {React.ReactElement} Sidepanel with detailed user information including:
 *   - User metadata and descriptions
 *   - Quick navigation links to related entitlements and violations
 *   - User status and role indicators with color-coded chips
 *   - Application information with logos
 *   - Department and manager information
 *   - Custom properties and user attributes
 *
 * @example
 * <UsersSidePanel entKeys={[123]} riskKeys={[456]} />
 *
 * @dependencies
 * - Redux: isSidePanelVisible, rowContext from state.aag.details.entitlementUsers.sidepanel
 * - Hooks: useInvertedLocalize, useNavigateTo, useGetQueryParams
 * - Components: SidePanel, SodFieldSet, SodSeverityChip, TextFieldSet, TextFieldValue
 */
function UsersSidePanel({
  entKeys,
  riskKeys,
}: UsersSidePanelProps): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const { navigateToDetails } = useNavigateTo();
  const queryParams = useGetQueryParams();
  const [getUserDetails, { isLoading }] = useGetUsersMutation();
  const [userDetails, setUserDetails] = useState<UserItem | null>(null);

  const {
    entitlementUsers: {
      sidepanel: { isSidePanelVisible, rowContext },
    },
    entitlementSummary: { selectedEntitlement, data: selectedEntitlementData },
  } = useTypedSelector((state) => state.aag.details);

  const entitlementDisplayName =
    selectedEntitlement?.entitlementDisplayName ??
    selectedEntitlementData?.entitlementDisplayName;

  const sidepanelRef = useRef(null);

  // Fetch specific user details when side panel opens
  useEffect(() => {
    if (isSidePanelVisible && rowContext.current?.userKey) {
      getUserDetails({
        includeDetails: true,
        includeViolationSummary: true,
        userKeys: [rowContext.current?.userKey],
        entitlementKeys: entKeys,
        riskKeys,
        columnToSort: 'userName',
        globalSearch: '',
        max: 25,
        offset: 0,
        order: 'ASC',
      })
        .unwrap()
        .then((response) => {
          if (response.data && response.data.length > 0) {
            setUserDetails(response.data[0]);
          }
        })
        .catch(() => {
          // Fallback to rowContext data if API fails
          setUserDetails(rowContext.current as UserItem | null);
        });
    } else {
      setUserDetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSidePanelVisible, rowContext, getUserDetails]);

  // Close sidepanel when component unmounts or navigation occurs
  useEffect(
    () => () => {
      if (isSidePanelVisible) {
        dispatch(
          detailsActions.setEntitlementUsersSidePanelRowContext({
            isSidePanelVisible: false,
            rowContext: {} as EntitlementUsersSidePanelGroupRowContextState,
          })
        );
      }
    },
    [dispatch, isSidePanelVisible]
  );

  const handleSidePanelClose = useCallback(
    () =>
      dispatch(
        detailsActions.setEntitlementUsersSidePanelRowContext({
          isSidePanelVisible: false,
          rowContext: {} as EntitlementUsersSidePanelGroupRowContextState,
        })
      ),
    [dispatch]
  );

  const handleViewViolations = useCallback(() => {
    const currentUser = userDetails || (rowContext.current as UserItem);

    if (currentUser) {
      // Add userName from current user or query parameters
      const userName = currentUser.userName || queryParams.userName;
      const params: {
        title: string;
        type: string;
        activeTab: string;
        entName: string;
        userName?: string;
      } = {
        title: userName,
        type: violationsEntitlement,
        activeTab: ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
        entName: entitlementDisplayName ?? '',
      };

      if (userName) {
        params.userName = userName;
      }

      navigateToDetails({
        key: String(rowContext.current.userKey),
        params,
      });
    }
  }, [
    userDetails,
    rowContext,
    entitlementDisplayName,
    queryParams.userName,
    navigateToDetails,
  ]);

  const sodSidePanelLayout = useCallback(
    (item: UserItem | null) => (
      <Box className='UsersSidePanel-contentBody'>
        {item?.userViolationDetails && (
          <Box className='UsersSidePanel-dataViz-wrapper'>
            <ViolationDonutChart
              item={item.userViolationDetails}
              onViewViolations={
                (item.userViolationDetails.violationCount ?? 0) > 0
                  ? handleViewViolations
                  : undefined
              }
            />
          </Box>
        )}
        {/* Field Sets */}
        <Box className='UsersSidePanel-sodFields'>
          <SodTextFieldSet label={l('Username')} value={item?.userName} />
          <SodTextFieldSet
            label={l('Display Name')}
            value={item?.displayName}
          />
          {item?.status && (
            <SodFieldSet
              label={l('User Status')}
              value={
                <Chip
                  size='xSmall'
                  kind='subtleFilled'
                  label={item.status}
                  color={
                    item.status?.toUpperCase() === l('Active').toUpperCase()
                      ? 'success'
                      : 'warning'
                  }
                />
              }
            />
          )}
          <SodTextFieldSet label={l('First Name')} value={item?.firstName} />
          <SodTextFieldSet label={l('Last Name')} value={item?.lastName} />
          <SodTextFieldSet
            label={l('System User Name')}
            value={item?.systemUserName}
          />
          <SodTextFieldSet label={l('Email')} value={item?.email} />
          <SodTextFieldSet label={l('Employee ID')} value={item?.employeeId} />
          <SodTextFieldSet label={l('Title')} value={item?.title} />
          <SodTextFieldSet label={l('Department')} value={item?.department} />
          <SodTextFieldSet
            label={l('Department Name')}
            value={item?.departmentName}
          />
          <SodTextFieldSet
            label={l('Employee Type')}
            value={item?.employeeType}
          />
          <SodTextFieldSet
            label={l('Accounts')}
            value={item?.accountsNames?.join(',')}
            lineLimit={2}
          />
        </Box>
      </Box>
    ),
    [l, handleViewViolations]
  );

  return (
    <Box className='UsersSidePanel-sidePanelWrapper--isFooterHidden'>
      <SidePanel
        ref={sidepanelRef}
        isOpen={isSidePanelVisible}
        onClose={() => {
          handleSidePanelClose();
        }}
        header={{
          title: (rowContext.current && rowContext.current.userName) ?? '',
          description: `${l('User with')} "${
            entitlementDisplayName || l('Entitlement')
          }"`,
          headerIcon: (
            <SodUserChip
              key='SodUserChip'
              firstName={
                (rowContext.current && rowContext.current.firstName) ?? ''
              }
              lastName={
                (rowContext.current && rowContext.current.lastName) ?? ''
              }
              userName={
                (rowContext.current && rowContext.current.userName) ?? ''
              }
              isPrefixInterpoint={false}
              avatarSize='medium'
            />
          ),
        }}
        appIconSize='medium'
        layout={
          !isLoading ? (
            sodSidePanelLayout(userDetails || (rowContext.current as UserItem))
          ) : (
            <Box className='Sod-Violations-loaderWrapper'>
              <Loader kind='dots' format='inline' color='brand' />
            </Box>
          )
        }
        isSidePanelNavigatingList
        isPreviousDisabled
        isNextDisabled
        isMobileWidth={false}
        isTabletWidth={false}
        className='UsersSidePanel-sidePanel'
        classNames={{
          layoutClassName: 'UsersSidePanel-sidePanelLayout',
          headerClassName: 'UsersSidePanel-sidePanelHeader',
          innerContentClassName: 'UsersSidePanel-sidePanelInnerContent',
          footerClassName: 'UsersSidePanel-sidePanelFooter',
        }}
        dataTestId={testIds.SOD_ENTITLEMENT_TAB_USERS_SIDE_PANEL}
      />
    </Box>
  );
}

export default UsersSidePanel;
